import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { createDiscountCode, updateDiscountCode } from "../../../services/discountService";
import { getStripeProducts } from "../../../services/apiAndBillingService";

const generateRandomCode = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let code = "";
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

const CreateDiscountCodePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData;
  console.log(editData)
  const isEdit = !!editData;
  const isLocked = isEdit && (editData?.redemptions > 0 || editData?.totalUsage?.used > 0);

  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState("");
  const [plans, setPlans] = useState([]);
  const [formData, setFormData] = useState({
    internalName: editData?.name || "",
    publicCode: editData?.code || "",
    discountType: editData?.discountType || (editData?.discountLabel?.includes("%") ? "percentage" : "fixed"),
    discountValue: editData?.discountValue || (editData?.discountLabel ? editData.discountLabel.replace(/[^0-9.]/g, '') : ""),
    plans: (editData?.plansIds || editData?.plans || []).map(p => typeof p === 'object' ? p.id : p),
    startDate: (editData?.startAt || editData?.startDate) ? new Date(editData.startAt || editData.startDate).toISOString().slice(0, 16) : "",
    noExpiry: editData?.noExpiry ?? !(editData?.endAt || editData?.expiryDate),
    expiryDate: (editData?.endAt || editData?.expiryDate) ? new Date(editData.endAt || editData.expiryDate).toISOString().slice(0, 16) : "",
    unlimitedGlobal: editData?.unlimitedUsage ?? (editData?.totalUsage ? editData.totalUsage.limit === null : true),
    globalCap: editData?.globalCap?.toString() || editData?.totalUsage?.limit?.toString() || "",
    perUserLimit: editData?.perUserLimit?.toString() || "",
    eligibility: editData?.eligibility === 'all_users' ? 'all_users' : (editData?.eligibility === 'new_users' ? 'new_users' : (editData?.eligibility === 'All users' ? 'all_users' : 'new_users')),
  });
  const [submitting, setSubmitting] = useState(false);
  const [dateError, setDateError] = useState("");
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const fetchPlans = async () => {
      setPlansLoading(true);
      setPlansError("");
      try {
        const data = await getStripeProducts({ limit: 100, active: true });
        const raw = Array.isArray(data) ? data : data?.products || data?.data || [];

        const normalized = raw
          .map((p) => ({
            planId: p._id || p.id || p.productId || p.stripeProductId,
            name: p.name || p.productName || "Untitled plan",
            priceText:
              p.priceText ||
              (p.defaultPrice?.unit_amount != null && p.defaultPrice?.currency
                ? `$${(p.defaultPrice.unit_amount / 100).toLocaleString()} ${String(
                  p.defaultPrice.currency
                ).toUpperCase()}`
                : p.price?.unit_amount != null && p.price?.currency
                  ? `$${(p.price.unit_amount / 100).toLocaleString()} ${String(
                    p.price.currency
                  ).toUpperCase()}`
                  : ""),
          }))
          .filter((p) => !!p.planId);

        setPlans(normalized);
      } catch (err) {
        setPlansError(err.message || "Failed to load plans");
        setPlans([]);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const togglePlan = (planId) => {
    setFormData((prev) => ({
      ...prev,
      plans: prev.plans.includes(planId)
        ? prev.plans.filter((p) => p !== planId)
        : [...prev.plans, planId],
    }));
    setFormErrors((prev) => ({ ...prev, plans: "" }));
  };

  const handleAutoGenerate = () => {
    handleChange("publicCode", generateRandomCode());
  };

  const goBack = () => {
    navigate("/admin-dashboard/discount-management");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDateError("");

    // Validate required fields
    const errors = {};
    if (!formData.internalName.trim()) {
      errors.internalName = "Internal name is required";
    }
    if (!formData.publicCode.trim()) {
      errors.publicCode = "Public code is required";
    } else if (!/^[A-Z0-9]+$/.test(formData.publicCode)) {
      errors.publicCode = "Discount code must be alphanumeric (A-Z, 0-9)";
    }
    if (!formData.startDate) {
      errors.startDate = "Start date is required";
    }
    if (!formData.discountValue && formData.discountValue !== 0) {
      errors.discountValue = "Discount value is required";
    } else if (Number(formData.discountValue) <= 0) {
      errors.discountValue = "Discount value must be greater than 0";
    } else if (formData.discountType === "percentage" && Number(formData.discountValue) > 100) {
      errors.discountValue = "Percentage discount cannot exceed 100%";
    }
    if (formData.plans.length === 0) {
      errors.plans = "Please select at least one plan";
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error("Please fill in all required fields correctly.");
      return;
    }
    setFormErrors({});

    // Validate dates
    if (!formData.noExpiry && formData.expiryDate) {
      const expiry = new Date(formData.expiryDate);
      const start = formData.startDate ? new Date(formData.startDate) : new Date();

      if (expiry <= start) {
        const msg = formData.startDate ? "Expiry date must be after start date" : "Expiry date must be in the future";
        setDateError(msg);
        toast.error(msg);
        setSubmitting(false);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.internalName,
        code: formData.publicCode,
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        plans: formData.plans,
        startDate: formData.startDate || undefined,
        expiryDate: formData.noExpiry ? undefined : formData.expiryDate,
        unlimitedUsage: formData.unlimitedGlobal,
        globalCap: formData.globalCap || undefined,
        perUserLimit: formData.perUserLimit || undefined,
        eligibility: formData.eligibility,
      };

      if (isEdit) {
        await updateDiscountCode(editData._id, payload);
      } else {
        await createDiscountCode(payload);
      }
      goBack();
    } catch (err) {
      console.error(isEdit ? "Update discount failed:" : "Create discount failed:", err);
      toast.error(err?.response?.data?.message || err?.message || (isEdit ? "Failed to update discount code." : "Failed to create discount code."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen p-3 sm:p-4 md:p-6">
      {/* Back To Code List */}
      <button
        onClick={goBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 py-4 text-sm font-medium"
      >
        <ArrowLeft className="w-4 h-4" />
        Back To Code List
      </button>

      <div className="max-w-8xl">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{isEdit ? "Edit Discount Code" : "Create Discount Code"}</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Internal Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="e.g Launch Week Campaign"
                  value={formData.internalName}
                  onChange={(e) => { handleChange("internalName", e.target.value); setFormErrors((prev) => ({ ...prev, internalName: "" })); }}
                  disabled={isLocked}
                  className={`w-full px-4 py-2.5 bg-gray-50 border rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-secondary focus:border-transparent ${formErrors.internalName ? "border-red-500" : "border-[#E0E4E2]"} ${isLocked ? "opacity-60 cursor-not-allowed bg-gray-100" : ""}`}
                />
                {isLocked && (
                  <p className="mt-1.5 text-xs text-orange-600 font-medium italic">
                    * The internal name is locked because this code has redemptions.
                  </p>
                )}
                {formErrors.internalName && (
                  <p className="mt-1.5 text-xs text-red-500">{formErrors.internalName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Public Code <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="e.g LAUNCH30"
                    value={formData.publicCode}
                    onChange={(e) => { 
                      const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                      handleChange("publicCode", val); 
                      setFormErrors((prev) => ({ ...prev, publicCode: "" })); 
                    }}
                    disabled={isLocked}
                    className={`flex-1 px-4 py-2.5 bg-gray-50 border rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-secondary focus:border-transparent uppercase ${formErrors.publicCode ? "border-red-500" : "border-[#E0E4E2]"} ${isLocked ? "opacity-60 cursor-not-allowed bg-gray-100" : ""}`}
                  />
                  {!isLocked && (
                    <button
                      type="button"
                      onClick={() => { handleAutoGenerate(); setFormErrors((prev) => ({ ...prev, publicCode: "" })); }}
                      className="p-2.5 bg-gray-50 border border-[#E0E4E2] rounded-lg hover:bg-gray-100 transition-colors"
                      title="Auto generate code"
                    >
                      <RefreshCw className="w-4 h-4 text-gray-600" />
                    </button>
                  )}
                </div>
                {isLocked && (
                  <p className="mt-1.5 text-xs text-orange-600 font-medium italic">
                    * The public code is locked because it has redemptions.
                  </p>
                )}
                {formErrors.publicCode ? (
                  <p className="mt-1.5 text-xs text-red-500">{formErrors.publicCode}</p>
                ) : (
                  <p className="mt-1.5 text-xs text-gray-500">
                    Codes are case in-sensitive and must be unique among active codes. Click on the
                    icon to auto generate codes.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Discount Configuration */}
          <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Discount Configuration</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Type
                </label>
                <div className="flex gap-6">
                  <label className={`flex items-center gap-2 ${isLocked ? "cursor-not-allowed" : "cursor-pointer"}`}>
                    <input
                      type="radio"
                      name="discountType"
                      checked={formData.discountType === "percentage"}
                      onChange={() => handleChange("discountType", "percentage")}
                      disabled={isLocked}
                      className={`w-4 h-4 text-secondary border-gray-300 focus:ring-secondary ${isLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                    />
                    <span className={`text-sm ${isLocked ? "text-gray-400" : "text-gray-700"}`}>Percentage</span>
                  </label>
                  <label className={`flex items-center gap-2 ${isLocked ? "cursor-not-allowed" : "cursor-pointer"}`}>
                    <input
                      type="radio"
                      name="discountType"
                      checked={formData.discountType === "fixed"}
                      onChange={() => handleChange("discountType", "fixed")}
                      disabled={isLocked}
                      className={`w-4 h-4 text-secondary border-gray-300 focus:ring-secondary ${isLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                    />
                    <span className={`text-sm ${isLocked ? "text-gray-400" : "text-gray-700"}`}>Fixed Amount</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Value <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-1">
                  {formData.discountType === "fixed" && (
                    <span className={`text-sm ${isLocked ? "text-gray-400" : "text-gray-500"}`}>$</span>
                  )}
                  <input
                    type="number"
                    placeholder="0"
                    value={formData.discountValue}
                    onChange={(e) => { handleChange("discountValue", e.target.value); setFormErrors((prev) => ({ ...prev, discountValue: "" })); }}
                    min="0"
                    max={formData.discountType === "percentage" ? "100" : undefined}
                    disabled={isLocked}
                    className={`flex-1 w-full px-4 py-2.5 bg-gray-50 border rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-secondary focus:border-transparent ${formErrors.discountValue ? "border-red-500" : "border-[#E0E4E2]"} ${isLocked ? "opacity-60 cursor-not-allowed bg-gray-100" : ""}`}
                  />
                  {formData.discountType === "percentage" && (
                    <span className={`text-sm ${isLocked ? "text-gray-400" : "text-gray-500"}`}>%</span>
                  )}
                </div>
                {isLocked && (
                  <p className="mt-2 text-[11px] text-orange-600 font-medium italic">
                    * This discount configuration is locked because the code has already been redeemed.
                  </p>
                )}
                {formErrors.discountValue && (
                  <p className="mt-1.5 text-xs text-red-500">{formErrors.discountValue}</p>
                )}
              </div>
            </div>
          </div>

          {/* Plan Applicability */}
          <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Plan Applicability <span className="text-red-500">*</span></h3>
              {formErrors.plans && (
                <p className="text-xs text-red-500">{formErrors.plans}</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {plansLoading ? (
                Array.from({ length: 4 }).map((_, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-2xl border border-[#E0E4E2] bg-gray-50 animate-pulse"
                  >
                    <div className="h-4 bg-gray-200 rounded w-2/3 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                  </div>
                ))
              ) : plansError ? (
                <div className="col-span-full p-4 rounded-2xl border border-red-200 bg-red-50">
                  <p className="text-sm text-red-700">{plansError}</p>
                  <p className="text-xs text-red-600 mt-1">
                    Couldn’t load plans from Stripe products API.
                  </p>
                </div>
              ) : plans.length === 0 ? (
                <div className="col-span-full p-4 rounded-2xl border border-[#E0E4E2] bg-gray-50">
                  <p className="text-sm text-gray-700">No active plans found.</p>
                </div>
              ) : (
                plans.map((plan) => (
                  <label
                    key={plan.planId}
                    className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-colors ${formData.plans.includes(plan.planId)
                      ? "border-secondary bg-green-50/30"
                      : "border-[#E0E4E2] bg-white hover:border-gray-300"
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.plans.includes(plan.planId)}
                      onChange={() => togglePlan(plan.planId)}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-secondary focus:ring-secondary"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{plan.name}</p>
                      {plan.priceText ? (
                        <p className="text-xs text-gray-500 mt-0.5">{plan.priceText}</p>
                      ) : (
                        <p className="text-xs text-gray-400 mt-0.5"> </p>
                      )}
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Time Controls */}
          <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Time Controls</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date And Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={formData.startDate}
                  onChange={(e) => { handleChange("startDate", e.target.value); setFormErrors((prev) => ({ ...prev, startDate: "" })); }}
                  disabled={isLocked}
                  className={`w-full px-4 py-2.5 bg-gray-50 border rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-secondary focus:border-transparent ${formErrors.startDate ? "border-red-500" : "border-[#E0E4E2]"} ${isLocked ? "opacity-60 cursor-not-allowed bg-gray-100" : ""}`}
                />
                {formErrors.startDate ? (
                  <p className="mt-1.5 text-xs text-red-500">{formErrors.startDate}</p>
                ) : (
                  <p className="mt-1.5 text-xs text-gray-500">Timezone : UTC</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => !isLocked && handleChange("noExpiry", !formData.noExpiry)}
                  disabled={isLocked}
                  className={`relative w-11 h-6 rounded-full transition-colors ${formData.noExpiry ? "bg-secondary" : "bg-gray-200"
                    } ${isLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.noExpiry ? "left-6" : "left-1"
                      }`}
                  />
                </button>
                <span className={`text-sm font-medium ${isLocked ? "text-gray-400" : "text-gray-700"}`}>No Expiry</span>
              </div>
              {!formData.noExpiry && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date And Time
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.expiryDate}
                    onChange={(e) => handleChange("expiryDate", e.target.value)}
                    disabled={isLocked}
                    className={`w-full px-4 py-2.5 bg-gray-50 border rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-secondary focus:border-transparent ${dateError ? "border-red-500" : "border-[#E0E4E2]"} ${isLocked ? "opacity-60 cursor-not-allowed bg-gray-100" : ""}`}
                  />
                  {dateError && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      {dateError}
                    </p>
                  )}
                </div>
              )}
              {isLocked && (
                <p className="mt-2 text-[11px] text-orange-600 font-medium italic">
                  * Time controls are locked because this code has been used.
                </p>
              )}
            </div>
          </div>

          {/* Usage Limit */}
          <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Limit</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => handleChange("unlimitedGlobal", !formData.unlimitedGlobal)}
                  className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 mt-0.5 ${formData.unlimitedGlobal ? "bg-secondary" : "bg-gray-200"
                    }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.unlimitedGlobal ? "left-6" : "left-1"
                      }`}
                  />
                </button>
                <div>
                  <p className="text-sm font-medium text-gray-700">Unlimited Global Usage</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Total redemption across all users
                  </p>
                </div>
              </div>
              {!formData.unlimitedGlobal && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Global Cap Usage
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    value={formData.globalCap}
                    onChange={(e) => handleChange("globalCap", e.target.value)}
                    min="0"
                    className="w-full px-4 py-2.5 bg-gray-50 border border-[#E0E4E2] rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-secondary focus:border-transparent"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Per User limit
                </label>
                <input
                  type="number"
                  placeholder="0"
                  value={formData.perUserLimit}
                  onChange={(e) => handleChange("perUserLimit", e.target.value)}
                  min="0"
                  className="w-full px-4 py-2.5 bg-gray-50 border border-[#E0E4E2] rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
                <p className="mt-1.5 text-xs text-gray-500">Max redemption per user account</p>
              </div>
            </div>
          </div>

          {/* User Eligibility */}
          <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">User Eligibility</h3>
            <div className="space-y-4">
              <label
                className={`flex items-start gap-3 p-4 rounded-2xl border transition-colors ${formData.eligibility === "new_users"
                  ? "border-secondary bg-green-50/30"
                  : "border-[#E0E4E2] bg-white hover:border-gray-300"
                  } ${isLocked ? "cursor-not-allowed" : "cursor-pointer"}`}
              >
                <input
                  type="radio"
                  name="eligibility"
                  checked={formData.eligibility === "new_users"}
                  onChange={() => handleChange("eligibility", "new_users")}
                  disabled={isLocked}
                  className="mt-1 w-4 h-4 text-secondary border-gray-300 focus:ring-secondary"
                />
                <div className={isLocked ? "opacity-60" : ""}>
                  <p className="text-sm font-medium text-gray-900">New Users Only</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    User has never had a successful paid subscription. Active, Cancelled or
                    expired subscribers are not eligible. Cancellation does not reset eligibility.
                  </p>
                </div>
              </label>
              <label
                className={`flex items-start gap-3 p-4 rounded-2xl border transition-colors ${formData.eligibility === "all_users"
                  ? "border-secondary bg-green-50/30"
                  : "border-[#E0E4E2] bg-white hover:border-gray-300"
                  } ${isLocked ? "cursor-not-allowed" : "cursor-pointer"}`}
              >
                <input
                  type="radio"
                  name="eligibility"
                  checked={formData.eligibility === "all_users"}
                  onChange={() => handleChange("eligibility", "all_users")}
                  disabled={isLocked}
                  className="mt-1 w-4 h-4 text-secondary border-gray-300 focus:ring-secondary"
                />
                <div className={isLocked ? "opacity-60" : ""}>
                  <p className="text-sm font-medium text-gray-900">All Users</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Any user can use this code regardless of their subscription history.
                  </p>
                </div>
              </label>
              {isLocked && (
                <p className="mt-2 text-[11px] text-orange-600 font-medium italic px-1">
                  * User eligibility is locked because this code has been used.
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={goBack}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 border border-[#E0E4E2] rounded-2xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2.5 text-sm font-medium text-white bg-secondary rounded-2xl hover:bg-secondary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (isEdit ? "Saving..." : "Creating...") : (isEdit ? "Save Changes" : "Create Code")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateDiscountCodePage;
